/**
 * TranslationContext - Comprehensive Translation Provider
 *
 * Provides a unified translation system that:
 * 1. Loads static translations from JSON files (fast initial load)
 * 2. Fetches database translations on login/language change
 * 3. Merges them with database taking precedence (allows admin overrides)
 * 4. Caches translations in localStorage for offline/fast reload
 * 5. Provides hooks for components to access translations
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/services/apiClient';
import { SUPPORTED_LANGUAGES, getTranslationSync } from '@/src/i18n/translationFallback';

// ============================================================================
// Types
// ============================================================================

interface TranslationContextType {
  // Current language
  language: string;
  setLanguage: (lang: string) => Promise<void>;

  // Translation function. The implementation accepts (key), (key, fallback),
  // (key, options) and (key, fallback, options) — the third form is how a
  // string with {{interpolation}} carries its values while staying
  // translatable. The declaration used to stop at two parameters, so every
  // call that needed interpolation had to be written as an untranslatable
  // template literal instead (TRUS-446).
  t: (
    key: string,
    fallbackOrOptions?: string | Record<string, unknown>,
    extraOptions?: Record<string, unknown>
  ) => string;

  // Available languages
  availableLanguages: Language[];

  // Loading states
  isLoading: boolean;
  isInitialized: boolean;

  // Database translations (for admin override)
  dbTranslations: Record<string, string>;

  // Refresh translations from database
  refreshTranslations: () => Promise<void>;

  // Get translation with metadata
  getTranslationInfo: (key: string) => TranslationInfo | null;
}

interface Language {
  code: string;
  name: string;
  nativeName: string;
  isActive: boolean;
  isDefault: boolean;
  translationProgress: number;
}

interface TranslationInfo {
  key: string;
  text: string;
  source: 'static' | 'database' | 'fallback';
  language: string;
}

interface TranslationProviderProps {
  children: ReactNode;
}

// ============================================================================
// Context
// ============================================================================

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

// Cache key for localStorage
const CACHE_KEY = 'weavein_translations';
const CACHE_EXPIRY_KEY = 'weavein_translations_expiry';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

// Languages cache (separate from translations)
const LANGUAGES_CACHE_KEY = 'weavein_languages';
const LANGUAGES_CACHE_EXPIRY_KEY = 'weavein_languages_expiry';
const LANGUAGES_CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour

// API timeout to prevent connection pool exhaustion
const API_TIMEOUT_MS = 5000; // 5 seconds

// Global initialization flag to prevent duplicate API calls
let isGloballyInitialized = false;

// ============================================================================
// Helper: Fetch with timeout
// ============================================================================
const fetchWithTimeout = async <T,>(
  fetchFn: () => Promise<T>,
  timeoutMs: number = API_TIMEOUT_MS
): Promise<T> => {
  return Promise.race([
    fetchFn(),
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
    ),
  ]);
};

// ============================================================================
// Provider Component
// ============================================================================

export function TranslationProvider({ children }: TranslationProviderProps) {
  const { t: i18nT, i18n } = useTranslation();

  const [language, setLanguageState] = useState(i18n?.language || 'en');
  const [dbTranslations, setDbTranslations] = useState<Record<string, string>>({});
  const [availableLanguages, setAvailableLanguages] = useState<Language[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // ============================================================================
  // Load cached translations
  // ============================================================================

  const loadCachedTranslations = useCallback((lang: string): Record<string, string> | null => {
    try {
      const cached = localStorage.getItem(`${CACHE_KEY}_${lang}`);
      const expiry = localStorage.getItem(`${CACHE_EXPIRY_KEY}_${lang}`);

      if (cached && expiry) {
        const expiryTime = parseInt(expiry, 10);
        if (Date.now() < expiryTime) {
          return JSON.parse(cached);
        }
      }
    } catch (error) {
      console.warn('Failed to load cached translations:', error);
    }
    return null;
  }, []);

  // ============================================================================
  // Save translations to cache
  // ============================================================================

  const saveCachedTranslations = useCallback((lang: string, translations: Record<string, string>) => {
    try {
      localStorage.setItem(`${CACHE_KEY}_${lang}`, JSON.stringify(translations));
      localStorage.setItem(`${CACHE_EXPIRY_KEY}_${lang}`, String(Date.now() + CACHE_DURATION_MS));
    } catch (error) {
      console.warn('Failed to cache translations:', error);
    }
  }, []);

  // ============================================================================
  // Fetch translations from database
  // ============================================================================

  const fetchDbTranslations = useCallback(async (lang: string): Promise<Record<string, string>> => {
    try {
      // Fetch with timeout to prevent connection pool exhaustion
      const response = await fetchWithTimeout(
        () => apiClient.get(`/api/translations/ui/${lang}`),
        API_TIMEOUT_MS
      );
      return response.data || {};
    } catch (error) {
      console.warn('Failed to fetch database translations:', error);
      return {};
    }
  }, []);

  // ============================================================================
  // Load cached languages
  // ============================================================================

  const loadCachedLanguages = useCallback((): Language[] | null => {
    try {
      const cached = localStorage.getItem(LANGUAGES_CACHE_KEY);
      const expiry = localStorage.getItem(LANGUAGES_CACHE_EXPIRY_KEY);

      if (cached && expiry) {
        const expiryTime = parseInt(expiry, 10);
        if (Date.now() < expiryTime) {
          return JSON.parse(cached);
        }
      }
    } catch (error) {
      console.warn('[i18n] Failed to load cached languages:', error);
    }
    return null;
  }, []);

  // ============================================================================
  // Save languages to cache
  // ============================================================================

  const saveCachedLanguages = useCallback((languages: Language[]) => {
    try {
      localStorage.setItem(LANGUAGES_CACHE_KEY, JSON.stringify(languages));
      localStorage.setItem(LANGUAGES_CACHE_EXPIRY_KEY, String(Date.now() + LANGUAGES_CACHE_DURATION_MS));
    } catch (error) {
      console.warn('[i18n] Failed to cache languages:', error);
    }
  }, []);

  // ============================================================================
  // Fetch available languages
  // ============================================================================

  const fetchAvailableLanguages = useCallback(async () => {
    // Build the complete list of AI-supported languages
    const aiLanguages: Language[] = SUPPORTED_LANGUAGES.map((lang) => ({
      code: lang.code,
      name: lang.name,
      nativeName: lang.nativeName,
      isActive: true,
      isDefault: lang.code === 'en',
      // English is 100%, others use AI translation fallback
      translationProgress: lang.code === 'en' ? 100 : 0,
    }));

    // Check cache first
    const cachedLanguages = loadCachedLanguages();
    if (cachedLanguages && cachedLanguages.length > 0) {
      console.log('[i18n] Using cached languages:', cachedLanguages.length);
      setAvailableLanguages(cachedLanguages);
      return;
    }

    console.log('[i18n] AI supported languages:', aiLanguages.length, aiLanguages.map(l => l.code));

    try {
      // Fetch with timeout to prevent connection pool exhaustion
      const response = await fetchWithTimeout(
        () => apiClient.get('/api/languages'),
        API_TIMEOUT_MS
      );
      const dbData = Array.isArray(response.data) ? response.data : [];
      console.log('[i18n] DB languages response:', dbData.length);

      if (dbData.length > 0) {
        // Map DB languages
        const dbLanguages = dbData.map((lang: any) => ({
          code: lang.language_code,
          name: lang.language_name,
          nativeName: lang.native_name,
          isActive: lang.is_active,
          isDefault: lang.is_default,
          translationProgress: lang.translation_progress || 0,
        }));

        // Merge: DB languages take precedence, then add AI languages not in DB
        const mergedLanguages: Language[] = [];
        const dbCodes = new Set(dbLanguages.map((l: Language) => l.code));

        // Add all DB languages first (they have official translations)
        mergedLanguages.push(...dbLanguages);

        // Add AI-supported languages that aren't in the database
        for (const aiLang of aiLanguages) {
          if (!dbCodes.has(aiLang.code)) {
            mergedLanguages.push(aiLang);
          }
        }

        console.log('[i18n] Merged languages:', mergedLanguages.length);
        setAvailableLanguages(mergedLanguages);
        saveCachedLanguages(mergedLanguages);
      } else {
        // No DB languages, use all AI languages
        console.log('[i18n] No DB languages, using AI languages');
        setAvailableLanguages(aiLanguages);
        saveCachedLanguages(aiLanguages);
      }
    } catch (error) {
      console.warn('[i18n] Failed to fetch languages from API, using AI translation fallback languages:', error);
      // Use all AI-supported languages as fallback
      setAvailableLanguages(aiLanguages);
      saveCachedLanguages(aiLanguages);
    }
  }, [loadCachedLanguages, saveCachedLanguages]);

  // ============================================================================
  // Refresh translations
  // ============================================================================

  const refreshTranslations = useCallback(async () => {
    setIsLoading(true);
    try {
      // First try cache
      let translations = loadCachedTranslations(language);

      // If no cache or expired, fetch from database
      if (!translations) {
        translations = await fetchDbTranslations(language);
        if (Object.keys(translations).length > 0) {
          saveCachedTranslations(language, translations);
        }
      }

      setDbTranslations(translations || {});
    } catch (error) {
      console.error('Failed to refresh translations:', error);
    } finally {
      setIsLoading(false);
    }
  }, [language, loadCachedTranslations, fetchDbTranslations, saveCachedTranslations]);

  // ============================================================================
  // Set language
  // ============================================================================

  const setLanguage = useCallback(async (lang: string) => {
    setIsLoading(true);
    try {
      // Change i18n language
      if (i18n?.changeLanguage) {
        await i18n.changeLanguage(lang);
      }
      setLanguageState(lang);

      // Clear old cache and fetch new translations
      const translations = await fetchDbTranslations(lang);
      setDbTranslations(translations);
      saveCachedTranslations(lang, translations);

    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsLoading(false);
    }
  }, [i18n, fetchDbTranslations, saveCachedTranslations]);

  // ============================================================================
  // Translation function with database override support
  // ============================================================================

  // Helper function to convert underscore/camelCase keys to human-readable text
  const toHumanReadable = useCallback((text: string): string => {
    return text
      .replace(/_/g, ' ')  // Replace underscores with spaces
      .replace(/([a-z])([A-Z])/g, '$1 $2')  // Add space before capitals (camelCase)
      .replace(/\b\w/g, (char) => char.toUpperCase());  // Capitalize first letter of each word
  }, []);

  // Check if a translation value looks like a raw key (has underscores, no spaces)
  const isRawKey = useCallback((value: string): boolean => {
    // If it contains underscores and no spaces, it's likely a raw key
    return value.includes('_') && !value.includes(' ');
  }, []);

  const t = useCallback((key: string, fallbackOrOptions?: string | Record<string, unknown>, extraOptions?: Record<string, unknown>): string => {
    // Parse arguments - support (key, fallback), (key, options), and (key, fallback, options)
    let fallback: string | undefined;
    let options: Record<string, unknown> = {};

    if (typeof fallbackOrOptions === 'string') {
      fallback = fallbackOrOptions;
      if (extraOptions && typeof extraOptions === 'object') {
        options = extraOptions;
      }
    } else if (typeof fallbackOrOptions === 'object' && fallbackOrOptions !== null) {
      options = fallbackOrOptions;
    }

    // Extract the last part of the key for comparison
    const keyParts = key.split('.');
    const lastPart = keyParts[keyParts.length - 1];

    // 1. Check database translations first (admin overrides)
    if (dbTranslations[key]) {
      let dbValue = dbTranslations[key];
      // If DB value is just the raw key, convert to human-readable
      if (isRawKey(dbValue)) {
        dbValue = toHumanReadable(dbValue);
      }
      // Apply interpolation if options provided
      if (Object.keys(options).length > 0) {
        Object.entries(options).forEach(([k, v]) => {
          dbValue = dbValue.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), String(v));
        });
      }
      return dbValue;
    }

    // 2. Check i18n static translations (with interpolation support)
    const i18nOptions = { defaultValue: '', ...options };
    const staticTranslation = i18nT(key, i18nOptions);
    if (staticTranslation && staticTranslation !== key) {
      // Check if the static translation is just a raw key (codemod issue)
      // e.g., "member_enrollments" should become "Member Enrollments"
      if (isRawKey(staticTranslation) && (staticTranslation === lastPart || staticTranslation.includes('_'))) {
        const humanReadable = toHumanReadable(staticTranslation);
        // For non-English, also try AI translation
        if (language !== 'en') {
          return getTranslationSync(key, humanReadable, language);
        }
        return humanReadable;
      }
      // Valid translation found
      if (language !== 'en' && staticTranslation === lastPart) {
        // No translation for this language, try AI fallback
        return getTranslationSync(key, toHumanReadable(staticTranslation), language);
      }
      return staticTranslation;
    }

    // 3. If fallback provided, use it (or AI translate for non-English)
    if (fallback) {
      // Apply interpolation to fallback if options provided
      let result = fallback;
      if (Object.keys(options).length > 0) {
        Object.entries(options).forEach(([k, v]) => {
          result = result.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), String(v));
        });
      }
      if (language !== 'en') {
        return getTranslationSync(key, result, language);
      }
      return result;
    }

    // 4. No fallback provided - convert key to human-readable format
    // e.g., "settings.member_enrollments" -> "Member Enrollments"
    const humanReadable = toHumanReadable(lastPart);

    // For non-English, try AI translation
    if (language !== 'en') {
      return getTranslationSync(key, humanReadable, language);
    }

    return humanReadable;
  }, [dbTranslations, i18nT, language, toHumanReadable, isRawKey]);

  // ============================================================================
  // Get translation info (for debugging/admin)
  // ============================================================================

  const getTranslationInfo = useCallback((key: string): TranslationInfo | null => {
    // Check database first
    if (dbTranslations[key]) {
      return {
        key,
        text: dbTranslations[key],
        source: 'database',
        language,
      };
    }

    // Check static
    const staticTranslation = i18nT(key, { defaultValue: '' });
    if (staticTranslation && staticTranslation !== key) {
      return {
        key,
        text: staticTranslation,
        source: 'static',
        language,
      };
    }

    return {
      key,
      text: key,
      source: 'fallback',
      language,
    };
  }, [dbTranslations, i18nT, language]);

  // ============================================================================
  // Initialize on mount
  // ============================================================================

  useEffect(() => {
    // Prevent duplicate initialization across React Strict Mode double-mount
    if (isGloballyInitialized) {
      console.log('[i18n] Skipping duplicate initialization');
      // Still load cached data for this instance
      const cachedLangs = loadCachedLanguages();
      if (cachedLangs) setAvailableLanguages(cachedLangs);
      const cachedTrans = loadCachedTranslations(language);
      if (cachedTrans) setDbTranslations(cachedTrans);
      setIsInitialized(true);
      return;
    }

    const initialize = async () => {
      isGloballyInitialized = true;
      setIsLoading(true);
      try {
        // Load cached data first for instant UI
        const cachedLangs = loadCachedLanguages();
        if (cachedLangs && cachedLangs.length > 0) {
          setAvailableLanguages(cachedLangs);
        }
        const cachedTrans = loadCachedTranslations(language);
        if (cachedTrans) {
          setDbTranslations(cachedTrans);
        }

        // Fetch fresh data in PARALLEL if no cache exists (performance optimization)
        const needsLanguages = !cachedLangs || cachedLangs.length === 0;
        const needsTranslations = !cachedTrans;

        if (needsLanguages || needsTranslations) {
          const [, freshTranslations] = await Promise.all([
            needsLanguages ? fetchAvailableLanguages() : Promise.resolve(),
            needsTranslations ? fetchDbTranslations(language) : Promise.resolve({}),
          ]);

          // Apply fresh translations if we fetched them
          if (needsTranslations && freshTranslations && typeof freshTranslations === 'object' && Object.keys(freshTranslations).length > 0) {
            setDbTranslations(freshTranslations as Record<string, string>);
            saveCachedTranslations(language, freshTranslations as Record<string, string>);
          }
        }

        setIsInitialized(true);
      } catch (error) {
        console.error('Failed to initialize translations:', error);
        setIsInitialized(true); // Still mark as initialized to allow app to function
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, [loadCachedLanguages, loadCachedTranslations, fetchAvailableLanguages, fetchDbTranslations, saveCachedTranslations, language]);

  // ============================================================================
  // Listen for i18n language changes
  // ============================================================================

  useEffect(() => {
    const handleLanguageChange = (lang: string) => {
      if (lang !== language) {
        setLanguageState(lang);
        refreshTranslations();
      }
    };

    if (i18n?.on) {
      i18n.on('languageChanged', handleLanguageChange);
    }
    return () => {
      if (i18n?.off) {
        i18n.off('languageChanged', handleLanguageChange);
      }
    };
  }, [i18n, language, refreshTranslations]);

  // ============================================================================
  // Context value
  // ============================================================================

  const value: TranslationContextType = {
    language,
    setLanguage,
    t,
    availableLanguages,
    isLoading,
    isInitialized,
    dbTranslations,
    refreshTranslations,
    getTranslationInfo,
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useAppTranslation() {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useAppTranslation must be used within a TranslationProvider');
  }
  return context;
}

/**
 * Like useAppTranslation, but safe outside a TranslationProvider (shared
 * renderers such as FormRenderer are also mounted in isolated previews and
 * tests). Without a provider, t() returns the English fallback.
 */
export function useOptionalAppTranslation(): Pick<TranslationContextType, 't'> {
  const context = useContext(TranslationContext);
  if (context) return context;
  const t = (key: string, fallbackOrOptions?: string | Record<string, unknown>, extraOptions?: Record<string, unknown>) => {
    const text = typeof fallbackOrOptions === 'string' ? fallbackOrOptions : key;
    const vars = (typeof fallbackOrOptions === 'object' ? fallbackOrOptions : extraOptions) || {};
    return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (_m, name) => (name in vars ? String(vars[name]) : ''));
  };
  return { t };
}

// ============================================================================
// Higher-order component for class components
// ============================================================================

export function withTranslation<P extends object>(
  WrappedComponent: React.ComponentType<P & { t: (key: string, fallbackOrOptions?: string | Record<string, unknown>) => string; language: string }>
) {
  return function WithTranslationComponent(props: P) {
    const { t, language } = useAppTranslation();
    return <WrappedComponent {...props} t={t} language={language} />;
  };
}

export default TranslationContext;
